import * as ng from 'angular';
import * as moment from 'moment';
import * as _ from 'underscore';
import * as uir from '@uirouter/angularjs';

import { GuestTypeService, GuestType } from '../guest-type';
import { UtilsService, defaultDateInputFormat, GeocodeService, GetTimezoneService } from '../../shared';

const categoriesJson = require('./categories.json');
const fcaCategoriesJson = require('./fca-categories.json');

declare const google: any;

export class EventEditController {
   static $inject = [
      '$stateParams', '$state', '$q',
      'geocodeService', 'getTimezoneService', 'eventsApi', 'GuestTypeService', 'utils',
      'dialogs', 'session', 'toast', 'loader', 'titleService', '$timeout'
   ];

   constructor (
      $stateParams:uir.StateParams,
      private $state:uir.StateService,
      private $q:ng.IQService,
      private geocodeService:GeocodeService,
      private getTimezone:GetTimezoneService,
      private eventsApi,
      guestTypeService:GuestTypeService,
      private utils:UtilsService,
      private dialogs,
      private session,
      private toast,
      private loader,
      titleService,
      private $timeout:ng.ITimeoutService
   ) {
      titleService.setTitle('Event Management');

      this.brand = this.session.getBrand();
      this.campaign = this.session.getCampaign();
      this.selectedLanguage = this.campaign.defaultLanguage; // used on the Content tab
      this.orgSlug = this.session.getOrg().slug;

      this.event = {
         campaignId: this.campaign.id,
         state: {},
         enableCloseTime: false,
         preRegCloseTimeEdit: moment().hours(19).minutes(0).format('LT'),
         preRegCloseDayOffset: 0,
         venueType: null,
         settings: {
            eventName: {},
            locationDescription: {}
         }
      };

      guestTypeService.list(this.campaign.id).then((guestTypes) => {
         this.guestTypes = guestTypes;
      });

      // initialize timezone list
      this.onCountryChange();

      if ($stateParams.id) {
         if ($state.current.name == 'event.copy') {
            this.mode = 'duplicate';
         } else {
            this.mode = 'edit';
         }

         this.getExistingEvent($stateParams.id);
      } else {
         this.mode = 'add';
      }

      if (this.orgSlug == 'fca' || this.orgSlug == 'fca-canada') {
         this.categories = fcaCategoriesJson;
      } else {
         this.categories = categoriesJson;
      }
   }

   mode:'add'|'edit'|'duplicate';

   brand;
   campaign;
   selectedLanguage:string;
   orgSlug:string;
   guestTypes:GuestType[];
   event;
   countryCode = 'US';
   invalidLocation = false;
   showGenerateMapsLink = false;

   timezones:{id:string,name:string,abbr:string,offset:string}[];

   categories:any[];

   get subCategory() {
      if (this.event.category) {
         let category = _.findWhere(this.categories, {id: this.event.category });
         return category.subcategories;
      } else {
         return [];
      }
   }

   fcaActivations = [
      'R&D Pro Driver Closed Course',
      'R&D Consumer Closed Course',
      'R&D Consumer Public Course',
      'Static'
   ];

   get languages():string[] {
      return this.campaign.settings.languages;
   }

   get startDateTime() {
      if (this.event.startDateTime) {
         let m = this.utils.toServerMoment(this.event.startDateTime);
         return m.format(defaultDateInputFormat);
      }
   }

   set startDateTime(val:string) {
      this.event.startDateTime = this.utils.toServerDateTime(val);
   }

   get endDateTime() {
      if (this.event.endDateTime) {
         let m = this.utils.toServerMoment(this.event.endDateTime);
         return m.format(defaultDateInputFormat);
      }
   }

   set endDateTime(val:string) {
      this.event.endDateTime = this.utils.toServerDateTime(val);
   }

   private getExistingEvent(eventId:number) {
      this.loader.showSpinner();

      this.eventsApi.get(eventId).then((data) => {
         if (data.preRegCloseTime !== null) {
            data.enableCloseTime = true;

            let hours = Math.floor(data.preRegCloseTime / 60);
            let minutes = data.preRegCloseTime % 60;
            let closeTime = moment();

            closeTime.hours(hours);
            closeTime.minutes(minutes);
            data.preRegCloseTimeEdit = closeTime.format('LT');
         } else {
            data.enableCloseTime = false;
            data.preRegCloseTimeEdit = moment().hour(19).minute(0).format('LT');
            data.preRegCloseDayOffset = 0;
         }

         if (this.mode === 'duplicate') {
            data.smsMessage = '';

            if (this.languages.length > 1) {
               let message = `The duplicated event includes copied language translations. Please review and update them under the Language Settings tab.`
               this.dialogs.alert(message);
            }
         }

         this.event = data;
         this.countryCode = data.state.countryCode;
         this.onCountryChange();
      }).finally(() => {
         this.loader.hide();
      });
   }

   private prepare():ng.IPromise<any> {
      let event = this.event;

      if (event.latLng) {
         event.lat = event.latLng[0];
         event.lng = event.latLng[1];
      }

      // null instead of empty string for "required validation"
      if (event.vendorContactEmail == '') {
         event.vendorContactEmail = null;
      }

      // null instead of empty array for "required validation"
      if (event.activationTypes && event.activationTypes.length == 0) {
         event.activationTypes = null;
      }

      // null instead of empty string for "url validation"
      if (event.directionsURL == '') {
         event.directionsURL = null;
      }

      if (event.enableCloseTime) {
         let closeTime = moment(event.preRegCloseTimeEdit, 'h:mm A');
         event.preRegCloseTime = (closeTime.hours() * 60) + closeTime.minutes();
      } else {
         event.preRegCloseTime = null;
         event.preRegCloseDayOffset = null;
      }

      if (this.mode === 'duplicate' && this.event.timeSlots && this.event.timeSlots.length > 0) {
         let eventStart = this.utils.toServerMoment(this.event.startDateTime);
         let eventEnd = this.utils.toServerMoment(this.event.endDateTime);

         let timeSlots = _.reject(this.event.timeSlots as any[], (ts) => {
            let tsStart = this.utils.toServerMoment(ts.startDateTime);
            let tsEnd = this.utils.toServerMoment(ts.endDateTime);
            return tsStart.isBefore(eventStart) || tsEnd.isAfter(eventEnd);
         });

         if (timeSlots.length !== this.event.timeSlots.length) {
            let message = 'The duplicated event has time slots outside the selected date range.';
            message += ' Click OK if you want to remove them and save.';

            return this.dialogs.confirm(message).then(() => {
               event.timeSlots = timeSlots;
            });
         }
      }

      return this.$q.when();
   }

   private post(method:'create'|'update') {
      let apiCall = this.eventsApi[method].bind(this.eventsApi);
      return apiCall(this.event).catch((resp) => {
         if (resp.status == 409) {
            let message = resp.data.name.pop();
            message = `${message}. Click OK if you still want to ${method} this event anyway.`;

            return this.dialogs.confirm(message).then(() => {
               return apiCall(this.event, true);
            });
         } else {
            return this.$q.reject(resp);
         }
      });
   }

   onNameChange() {
      if (_.contains(this.event.name.toLowerCase().split(' '), 'test')) {
         this.event.testing = true;
      }

      if (this.languages.length > 1) {
         this.event.settings.eventName[this.campaign.defaultLanguage] = this.event.name;
      }
   }

   onEventSettingsChange(dest, val) {
      this.event[dest] = val;
   }

   onPickPlace(place) {
      let needRefreshCountry = this.countryCode != place.countryCode;

      this.showGenerateMapsLink = false;
      this.invalidLocation = false;
      this.event.latLng = place.point;
      this.event.address = place.address;
      this.event.address2 = place.address2;
      this.event.cityName = place.city;
      this.event.postalCode = place.postalCode;
      this.countryCode = place.countryCode;
      this.event.state.code = place.stateCode;
      this.event.directionsURL = `https://maps.google.com/?q=place_id:${place.placeId}`;

      if (this.campaign.defaultLanguage == 'en' && (this.countryCode == 'CA' || this.countryCode == 'US')) {
         this.event.settings.locationDescription[this.campaign.defaultLanguage] = `${this.event.address}, ${this.event.cityName}, ${this.event.state.code}`.trim();
      } else {
         this.event.settings.locationDescription[this.campaign.defaultLanguage] = `${this.event.address}, ${this.event.cityName}, ${this.event.state.code}, ${place.country}`.trim();
      }

      if (needRefreshCountry) {
          this.onCountryChange();
      }

      this.getTimezone(place.point).then(zone => {
         this.event.tzId = zone.timeZoneId;
      });

      if (this.languages.length > 1) {
         const service = new google.maps.places.PlacesService(document.createElement("div"));

         for (let language of this.languages) {
            service.getDetails(
               {
                  placeId: place.placeId,
                  language: language,
                  fields: ["formatted_address", "address_components"]
               },
               (place, status) => {
                  if (status === google.maps.places.PlacesServiceStatus.OK) {
                     this.$timeout(() => {
                        let countryCode = this.getCountryCode(place);
                        if (language == 'en' && (countryCode == 'CA' || countryCode == 'US')) {
                           this.event.settings.locationDescription[language] = this.getShortAddressFromComponents(place);
                        } else {
                           this.event.settings.locationDescription[language] = place.formatted_address;
                        }
                     });
                  } else {
                     console.error(`Failed to get details in ${language}:`, status);
                  }
               }
            );
         }
      }
   }

   private getShortAddressFromComponents(place) {
      if (!place || !place.address_components) return "";

      let street = "";
      let city = "";
      let state = "";

      place.address_components.forEach((component) => {
         if (component.types.includes("street_number")) {
            street = component.long_name + " ";
         }
         if (component.types.includes("route")) {
            street += component.short_name;
         }
         if (component.types.includes("locality")) {
            city = component.long_name;
         }
         if (component.types.includes("administrative_area_level_1")) {
            state = component.short_name;
         }
      });

      return `${street}, ${city}, ${state}`.trim();
   }
   
   private getCountryCode(place) {
      if (!place || !place.address_components) return "";
      for (const component of place.address_components) {
         if (component.types.includes("country")) {
            return component.short_name; // Returns country code (e.g., "US", "CA", "JP")
         }
      }
      return "";
   }

   getPreregTotal() {
      return _.reduce(this.event.settings.maxPreReg, (total, max) => {
         if (ng.isNumber(max)) {
            total = ng.isNumber(total) ? total : 0;
            total += max;
         }
         return total;
      }, this.event.maxPreReg);
   }

   onStateSelect(state) {
      this.event.state = state ? ng.copy(state) : {};
      this.event.stateId = state ? state.id : null;
   }

   onCountryChange() {
      this.timezones = _.chain(moment.tz.zonesForCountry(this.countryCode, true))
         .sortBy('name')
         .sortBy('offset')
         .map(z => this.mapZones(z.name))
         .value();
   }

   mapZones(tzId:string) {
      let m = moment.tz(tzId);
      // getting offset from this function instead of tz.offset provides an
      // offset that has the correct sign/polarity
      let offset = m.utcOffset() / 60;

      return {
         id: tzId,
         name: tzId.replace(/[^/]+\//, '').replace(/_/g, ' '),
         offset: m.utcOffset() > 0? '+' + offset : '' + offset,
         abbr: m.zoneAbbr()
      };
   }

   timezoneIdToLabel(tzId:string) {
      if (tzId) {
         let zoneInfo = this.mapZones(tzId)
         return `${zoneInfo.name} (${zoneInfo.abbr} UTC${zoneInfo.offset})`;
      }

      return null;
   }

   private regeocode() {
      this.invalidLocation = false;
      this.event.latLng = null;

      let address = this.event.address;
      let city = this.event.cityName;
      let state = this.event.state ? this.event.state.name : null;

      this.geocodeService(address, city, state).then((loc) => {
         this.event.latLng = loc;

         // TODO error handling?
         this.getTimezone(loc).then(zone => {
            this.event.tzId = zone.timeZoneId;
         });
      }).catch(() => this.invalidLocation = true);
   }

   onAddressChange() {
      this.showGenerateMapsLink = true;
      this.event.directionsURL = null;
      this.event.settings.locationDescription[this.campaign.defaultLanguage] = null;
   }

   onGenerateMapsLink() {
      this.showGenerateMapsLink = false;
      this.regeocode();
   
      const locationParts = [
         this.event.address,
         this.event.cityName,
         this.event.state?.code,
         this.countryCode
      ].filter(part => part);
   
      this.event.directionsURL = `http://maps.google.com?q=${locationParts.join(",")}`;
   }

   save(goBack) {
      if (this.invalidLocation) {
         return;
      }

      this.loader.showSpinner();

      let promise = this.prepare();

      if (this.mode == 'edit') {
         promise = promise.then(() => this.post('update'));
      } else {
         promise = promise.then(() => this.post('create'));
      }

      promise.then((result) => {
         this.toast.success()
            .message(this.mode == 'edit' ? 'Event saved!' : 'Event created!')
            .send();

         if (goBack) {
            this.$state.go('^.list');
         } else if (this.mode == 'add') {
            this.$state.go('event.edit', {id: result.eventID});
         }
      }).catch(error => {
         if (error.status == 400) {
            this.toast.error()
               .message('One or more errors detected. Please see the validation feedback and correct the errors.')
               .send();
         }
      }).finally(() => {
         this.loader.hide();
      });
   }

   isFieldRequired(fieldName:string) {
      return _.contains(this.brand.settings.requiredEventFields, fieldName);
   }

   get prizeAllocationType() {
      if (this.event.settings.prizeAllocation) {
         return this.event.settings.prizeAllocation;
      }

      return 'per-event'; // participant can win one prize per event is the default setting
   }

   set prizeAllocationType(val:string) {
      this.event.settings.prizeAllocation = val;
   }
}

ng.module('e360')
   .controller('EventEditController', EventEditController);
